import { generatePlaylist } from "@/components/ds/lib/DSPlaylistGenerator";
import { DSContentGroupMember, DSContentType, PlayerComputedContentOrContentGroup } from "@/components/ds/lib/types";
import { DSPlayer } from "@/components/ds/player/DSPlayer";
import { LoadingSmall } from "@/components/LoadingSmall";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { getContentGroupDetails, getContentGroupMembers, getScreenSize } from "@/sideEffects/api";
import { selectActiveFirm } from "@/store/appSelectors";
import { useQuery } from "@tanstack/react-query";
import { EyeIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

interface Props {
  contentGroupGuid: string;
}

export const ContentGroupPreviewPlaybackButton: React.FC<Props> = ({ contentGroupGuid }) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);
  const [showDialog, setShowDialog] = useState(false);

  const { data: members } = useQuery({
    queryKey: ["contentGroupMembers"],
    queryFn: () =>
      getContentGroupMembers(
        { pageNumber: 1, numberOfObjectsPerPage: 999999 },
        {
          FirmGuid: activeFirm?.firmGuid,
          ContentGroupGuid: contentGroupGuid,
        }
      ),
  });

  const contentGroupDetails = useQuery({
    queryKey: ["contentGroupDetails", String(activeFirm?.firmGuid), contentGroupGuid],
    queryFn: () => getContentGroupDetails(String(activeFirm?.firmGuid), String(contentGroupGuid)),
  });

  const contentGroup = contentGroupDetails.data?.data;
  const membersData = members?.data?.result || [];

  const { data: screenSizeData } = useQuery({
    queryFn: () =>
      getScreenSize(
        {
          numberOfObjectsPerPage: 1000,
          pageNumber: 1,
        },
        {
          Firm_Id: String(activeFirm?.firmGuid),
        }
      ),
    queryKey: ["getScreenSize"],
  });

  const screenSizeObj = screenSizeData?.data?.result?.find(
    (screenSize) => screenSize.id === contentGroup?.screenSizeId
  );

  if (!screenSizeObj) {
    return <LoadingSmall />;
  }

  const membersDataToPlayerComputedContentOrContentGroup: PlayerComputedContentOrContentGroup = {
    contentGroupId: contentGroupGuid,
    contentGroupintId: 0,
    contentGroupName: "",
    screenSizeintId: screenSizeObj?.intId,
    screenSizeId: screenSizeObj?.id,
    screenSizeWidth: screenSizeObj?.width,
    screenSizeHeight: screenSizeObj?.height,
    isShuffle: false,
    // @ts-ignore
    contentDetials: membersData.map(
      (member) =>
        ({
          contentId: member.contentGuid,
          contentintId: 0,
          contentStorageName: member.contentStorageName,
          contentDuration: member.duration,
          startDate: member.startDate,
          endDate: member.endDate,
          layoutConfig: member.layoutConfig,
          contentLink: member.contentLink,
          viewportSize: member.viewportSize,
          contentType: member.contentType as DSContentType,
          id: member.contentGroupMemberGuid,
          order: member.order,
          isActive: member.isActive,
        } satisfies DSContentGroupMember<string>)
    ),
  };
  const playlist = generatePlaylist([membersDataToPlayerComputedContentOrContentGroup], undefined, {
    width: screenSizeObj.width,
    height: screenSizeObj.height,
  });

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => {
          setShowDialog(true);
        }}
      >
        <EyeIcon className="w-4 h-4" />
        {t("content.preview")}
      </Button>
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="overflow-y-auto">
          <DialogHeader>
            <div style={{ width: screenSizeObj.width, height: screenSizeObj.height }}>
              <DSPlayer list={playlist} isFullScreen={true} />
            </div>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </>
  );
};
